import { useQuery, useQueryClient } from "@tanstack/react-query"
import { formatRubles } from "@velizhanin-dev/bot-scenario"
import { formatDistanceToNowStrict } from "date-fns"
import { ru } from "date-fns/locale/ru"
import { Loader2, Paperclip } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { MEDIA_KIND_LABELS } from "@/shared/config/media"
import { cn } from "@/shared/lib/utils"
import { Avatar, AvatarFallback } from "@/shared/ui/avatar"
import { Button } from "@/shared/ui/button"
import {
	type AudienceMember,
	audienceKeys,
	getAudienceFile,
	type MemberState,
	saveBlob,
} from "../api/audience-api"

export const STATE_META: Record<MemberState, { label: string; dot: string }> = {
	IDLE: { label: "Сценарий закончился", dot: "bg-muted-foreground/60" },
	WAITING_INPUT: { label: "Ждёт ответ", dot: "bg-sky-400" },
	WAITING_BUTTON: { label: "Ждёт нажатия кнопки", dot: "bg-sky-400" },
	WAITING_DELAY: { label: "На паузе", dot: "bg-violet-400" },
	WAITING_REQUEST: { label: "Ждёт ответ сервиса", dot: "bg-violet-400" },
	WAITING_PAYMENT: { label: "Ждёт оплату", dot: "bg-amber-400" },
	BLOCKED: { label: "Заблокировал бота", dot: "bg-red-400" },
}

export function StateLabel({ state, className }: { state: MemberState; className?: string }) {
	const meta = STATE_META[state] ?? { label: state, dot: "bg-muted-foreground" }
	return (
		<span className={cn("inline-flex items-center gap-1.5", className)}>
			<span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", meta.dot)} />
			{meta.label}
		</span>
	)
}

export function plural(count: number, forms: [string, string, string]): string {
	const mod10 = count % 10
	const mod100 = count % 100
	if (mod10 === 1 && mod100 !== 11) return forms[0]
	if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1]
	return forms[2]
}

export const rubles = (kopecks: number) => formatRubles(kopecks / 100)

export function memberName(
	member: Pick<AudienceMember, "firstName" | "lastName" | "username" | "telegramUserId">,
): string {
	return (
		[member.firstName, member.lastName].filter(Boolean).join(" ") ||
		(member.username ? `@${member.username}` : `ID ${member.telegramUserId}`)
	)
}

export function formatRelative(value: string | null): string {
	return value
		? formatDistanceToNowStrict(new Date(value), { addSuffix: true, locale: ru })
		: "ещё не писал"
}

const AVATAR_GRADIENTS = [
	"from-sky-400 to-blue-600",
	"from-emerald-400 to-teal-600",
	"from-amber-400 to-orange-600",
	"from-rose-400 to-pink-600",
	"from-violet-400 to-purple-600",
	"from-cyan-400 to-sky-600",
]

/** Аватар-инициал: цвет стабилен для одного пользователя. */
export function MemberAvatar({
	member,
	size = "md",
}: {
	member: Pick<AudienceMember, "firstName" | "lastName" | "username" | "telegramUserId">
	size?: "md" | "lg"
}) {
	let hash = 0
	for (const char of member.telegramUserId) hash = (hash * 31 + char.charCodeAt(0)) >>> 0
	const initial = (member.firstName || member.username || "?").charAt(0).toUpperCase()
	return (
		<Avatar aria-hidden className={size === "lg" ? "h-14 w-14" : "h-10 w-10"}>
			<AvatarFallback
				className={cn(
					"bg-gradient-to-br font-semibold text-white",
					AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length],
					size === "lg" ? "text-xl" : "text-sm",
				)}
			>
				{initial}
			</AvatarFallback>
		</Avatar>
	)
}

const KIND_LABELS = MEDIA_KIND_LABELS as Record<string, string>

function useBlobUrl(blob: Blob | undefined): string | null {
	const [url, setUrl] = useState<string | null>(null)
	useEffect(() => {
		if (!blob) return
		const next = URL.createObjectURL(blob)
		setUrl(next)
		return () => URL.revokeObjectURL(next)
	}, [blob])
	return url
}

/**
 * Файл, который пользователь прислал боту. Идёт через api (getFile): фото и стикер
 * показываются превью, остальное скачивается по нажатию.
 */
export function MemberFile({
	botId,
	sessionId,
	fileId,
	kind,
	fileName,
}: {
	botId: string
	sessionId: string
	fileId: string
	kind: string
	fileName?: string
}) {
	const queryClient = useQueryClient()
	const isImage = kind === "photo" || kind === "sticker"
	const queryKey = audienceKeys.file(botId, sessionId, fileId)
	const queryFn = () => getAudienceFile(botId, sessionId, fileId)
	const preview = useQuery({
		queryKey,
		queryFn,
		enabled: isImage,
		staleTime: Infinity,
		retry: false,
	})
	const url = useBlobUrl(preview.data)
	const [loading, setLoading] = useState(false)

	const download = async () => {
		setLoading(true)
		try {
			const blob = await queryClient.fetchQuery({ queryKey, queryFn, staleTime: Infinity })
			saveBlob(blob, fileName || `${kind}-${fileId.slice(-8)}`)
		} catch {
			toast.error("Не удалось получить файл из Telegram")
		} finally {
			setLoading(false)
		}
	}

	if (isImage) {
		if (url) {
			return (
				<a
					href={url}
					target="_blank"
					rel="noreferrer"
					className="block w-fit overflow-hidden rounded-xl border border-border/60"
				>
					<img src={url} alt="Фото от пользователя" className="max-h-48 max-w-full object-cover" />
				</a>
			)
		}
		return (
			<div className="grid h-24 w-32 place-items-center rounded-xl bg-muted/50 text-xs text-muted-foreground">
				{preview.isError ? "Файл недоступен" : <Loader2 className="h-4 w-4 animate-spin" />}
			</div>
		)
	}

	return (
		<Button
			type="button"
			variant="ghost"
			onClick={() => void download()}
			disabled={loading}
			className="h-auto max-w-full justify-start gap-2.5 rounded-xl bg-muted/50 px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-60"
		>
			<span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-sky-500/20 text-sky-300">
				{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
			</span>
			<span className="min-w-0">
				<span className="block truncate">{fileName || KIND_LABELS[kind] || "Файл"}</span>
				<span className="block text-xs text-muted-foreground">
					{KIND_LABELS[kind] ?? kind} · скачать
				</span>
			</span>
		</Button>
	)
}
